/**
 * What the user is looking at, shared by every window: the selection, the search, the
 * filters, the link tool... Windows never talk to each other; they all read and write this
 * one store. The graph window draws the selection, the inspector describes it, the filter
 * window changes the filters, and so on.
 *
 * Filters, the labels, groups and bundles switches, the spacing, the 2D/3D choice and the
 * view chosen are remembered in the browser. Which views exist here comes from the
 * environment worked in ({@link View.setViews}): the chosen one is shown when it is offered,
 * else the environment's first.
 */
import { batch, createMemo, createSignal } from "solid-js";
import { createStore } from "solid-js/store";
import type { Link } from "@protocol/Link";
import type { LinkKind } from "@protocol/LinkKind";
import { load, save } from "../shared/storage";
import type { CommandSet } from "../shared/commands";
import type { MenuSection, ToolbarButton } from "../shared/menus";
import type { Actions } from "../client/actions";
import type { GraphMirror } from "../client/graph";
import type { Group } from "@protocol/Group";
import { toast } from "../shared/toast";
import { clampSpacing, type Dimension } from "./scene";

/**
 * How the graph window shows the graph: the nodal graph (nodes in a live layout), a UML class
 * diagram (boxes in layers), or a blueprint (the diagram's boxes with pins, each pin wired to
 * where it leads, like Unreal Engine's Blueprints). Pictures of the same graph: same selection,
 * same filters, same edits; the diagram and the blueprint share their boxes' places. The board
 * (Miro's) places each item freely at its own size (`node.board`), with its own look (`style`).
 * The document view (Word's) writes the selected node's document, on sheets of paper.
 */
export type ViewMode = "nodal" | "diagram" | "blueprint" | "board" | "document";

/** Every view the GUI can draw, by the id of its plugin. */
export const VIEW_MODES: readonly ViewMode[] = ["nodal", "diagram", "blueprint", "board", "document"];

/** The words for each view (the view switcher's tooltips and the View menu). */
export const VIEW_LABELS: Record<ViewMode, string> = { nodal: "Nodal graph", diagram: "Class diagram", blueprint: "Blueprint", board: "Board", document: "Document" };

/** Whether `mode` draws the diagram's boxes (the class diagram and the blueprint). */
export const drawsBoxes = (mode: ViewMode | null): mode is "diagram" | "blueprint" => mode === "diagram" || mode === "blueprint";

/**
 * What is selected: a node, several nodes (the class diagram's box selection: two or more), a
 * link, a group, a bundle of links between groups, or nothing.
 */
export type Selection =
  | { type: "node"; id: string }
  | { type: "nodes"; ids: string[] }
  | { type: "link"; link: Link }
  | { type: "group"; id: string }
  | { type: "bundle"; key: string }
  | null;

/** The shared view state. */
export interface ViewState {
  selection: Selection;
  /** Search text (lower-case match on name or language). */
  query: string;
  /** The categories hidden, lower case (`""`: nodes in no category). New ones are shown. */
  hiddenCategories: string[];
  /** Link kinds hidden (`false`) or shown again (`true`); a kind not named is shown (`linkKindShown`). */
  linkKinds: Record<string, boolean>;
  /** Whether node names are drawn. */
  labels: boolean;
  /** Whether groups are drawn as circles around their nodes. */
  groups: boolean;
  /** Whether the links between two groups are drawn as one bundle. */
  bundles: boolean;
  /** Whether a drag on a node creates a link instead of moving it. */
  linkTool: boolean;
  /** Whether the graph is drawn flat or in 3D. */
  dimension: Dimension;
  /** How spread out the layout is (1 = usual, 2 = twice as far; `scene.spacing`). */
  spacing: number;
  /** The nodal graph or the class diagram. */
  mode: ViewMode;
  /** The group whose name is being written in place (the selection pill turns into a field), if any. */
  renaming: string | null;
}

/** Where the filters and the labels switch are kept in the browser. */
const PREFS_KEY = "developer-app-view-v1";

/** The view store and the commands windows can send to the graph window. */
export interface View {
  state: ViewState;
  selectNode(id: string | null): void;
  /** Selects these nodes (none: nothing, one: that node). */
  selectNodes(ids: readonly string[]): void;
  selectLink(link: Link | null): void;
  selectGroup(id: string | null): void;
  selectBundle(key: string | null): void;
  /** Selects the group and writes its name in place (`null`: stops). */
  renameGroup(id: string | null): void;
  setQuery(query: string): void;
  /** Hides a shown category, shows a hidden one (`""`: nodes in no category). */
  toggleCategory(name: string): void;
  toggleLinkKind(kind: LinkKind): void;
  /** Shows a category if it was hidden. */
  showCategory(name: string): void;
  showLinkKind(kind: LinkKind): void;
  setLabels(on: boolean): void;
  setGroups(on: boolean): void;
  setBundles(on: boolean): void;
  setLinkTool(on: boolean): void;
  setDimension(dimension: Dimension): void;
  /** Shows one of the views (remembered, shown when offered here). */
  setMode(mode: ViewMode): void;
  /** The view shown: the chosen one if this environment offers it, else its first; none offered: `null`. */
  mode(): ViewMode | null;
  /** The views offered here, in order. */
  views(): ViewMode[];
  /** Offers these views (plugin ids; the ones the GUI cannot draw are left out). */
  setViews(ids: readonly string[]): void;
  /** Spreads the layout out (more) or packs it (less); kept within the allowed range. */
  setSpacing(spacing: number): void;
  /** Asks the graph window to fit everything in view. Watch {@link View.fitRequests}. */
  requestFit(): void;
  /** Number of fit requests so far (the graph window reacts when it changes). */
  fitRequests: () => number;
  /** Asks the graph window to centre on a node. */
  requestCenter(id: string): void;
  /** The last centre request, with a counter so the same node can be asked twice. */
  centerRequest: () => { id: string; n: number } | null;
}

/** Creates the view store, restoring the saved filters. */
export function createView(): View {
  const saved = load<Partial<ViewState>>(PREFS_KEY, {});
  const [state, setState] = createStore<ViewState>({
    selection: null,
    query: "",
    hiddenCategories: Array.isArray(saved.hiddenCategories) ? saved.hiddenCategories : [],
    linkKinds: { ...saved.linkKinds },
    labels: saved.labels ?? true,
    groups: saved.groups ?? true,
    bundles: saved.bundles ?? true,
    linkTool: false,
    dimension: saved.dimension === "3d" ? "3d" : "2d",
    spacing: clampSpacing(saved.spacing ?? 1),
    mode: VIEW_MODES.includes(saved.mode as ViewMode) ? (saved.mode as ViewMode) : "nodal",
    renaming: null,
  });
  /** Remembers the filters, the switches and the 2D/3D choice in the browser. */
  const persist = () =>
    save(PREFS_KEY, { hiddenCategories: state.hiddenCategories, linkKinds: state.linkKinds, labels: state.labels, groups: state.groups, bundles: state.bundles, dimension: state.dimension, spacing: state.spacing, mode: state.mode });
  const [views, setViewList] = createSignal<ViewMode[]>([...VIEW_MODES], { equals: (a, b) => a.length === b.length && a.every((v, i) => v === b[i]) });
  const mode = createMemo(() => (views().includes(state.mode) ? state.mode : (views()[0] ?? null)));
  const [fitRequests, setFitRequests] = createSignal(0);
  const [centerRequest, setCenterRequest] = createSignal<{ id: string; n: number } | null>(null);

  /**
   * Replaces the selection with `selection`. A store merges an object set over another object
   * (same object, fields changed, old fields left behind), so a node chosen after another would
   * neither drop the old fields nor be seen by what watches the selection as a whole (the
   * graph's repaint): clearing it first makes every selection a new object.
   */
  const select = (selection: Selection) =>
    batch(() => {
      setState("selection", null);
      setState("selection", selection);
      if (selection?.type !== "group" || selection.id !== state.renaming) setState("renaming", null);
    });

  return {
    state,
    selectNode: (id) => select(id ? { type: "node", id } : null),
    selectNodes(ids) {
      const unique = [...new Set(ids)];
      select(unique.length > 1 ? { type: "nodes", ids: unique } : unique.length ? { type: "node", id: unique[0]! } : null);
    },
    selectLink: (link) => select(link ? { type: "link", link: { ...link } } : null),
    selectGroup: (id) => select(id ? { type: "group", id } : null),
    selectBundle: (key) => select(key ? { type: "bundle", key } : null),
    renameGroup(id) {
      batch(() => {
        if (id) select({ type: "group", id });
        setState("renaming", id);
      });
    },
    setQuery: (query) => setState("query", query.trim().toLowerCase()),
    toggleCategory(name) {
      const key = name.trim().toLowerCase();
      setState("hiddenCategories", (hidden) => (hidden.includes(key) ? hidden.filter((h) => h !== key) : [...hidden, key]));
      persist();
    },
    toggleLinkKind(kind) {
      setState("linkKinds", kind, (on) => on === false);
      persist();
    },
    showCategory(name) {
      const key = name.trim().toLowerCase();
      if (!state.hiddenCategories.includes(key)) return;
      setState("hiddenCategories", (hidden) => hidden.filter((h) => h !== key));
      persist();
    },
    showLinkKind(kind) {
      // Only a hidden kind changes: a filter change re-checks the selection, which must not
      // happen while the scene catches up with the edit that asked to show it.
      if (state.linkKinds[kind] !== false) return;
      setState("linkKinds", kind, true);
      persist();
    },
    setLabels(on) {
      setState("labels", on);
      persist();
    },
    setGroups(on) {
      setState("groups", on);
      persist();
    },
    setBundles(on) {
      setState("bundles", on);
      persist();
    },
    setLinkTool: (on) => setState("linkTool", on),
    setDimension(dimension) {
      setState("dimension", dimension);
      persist();
    },
    setMode(next) {
      setState("mode", next);
      persist();
    },
    mode,
    views,
    setViews: (ids) => setViewList(VIEW_MODES.filter((v) => ids.includes(v)).sort((a, b) => ids.indexOf(a) - ids.indexOf(b))),
    setSpacing(spacing) {
      setState("spacing", clampSpacing(spacing));
      persist();
    },
    requestFit: () => setFitRequests((n) => n + 1),
    fitRequests,
    requestCenter: (id) => setCenterRequest((last) => ({ id, n: (last?.n ?? 0) + 1 })),
    centerRequest,
  };
}

/** The view's menu lines: the selection (Edit), and how the graph is shown (View). */
export function viewMenus(view: View): MenuSection[] {
  const s = view.state;
  return [
    {
      menu: "edit",
      section: "Selection",
      order: 30,
      entries: () => [
        { label: "Find a node", command: "search.focus" },
        { label: "Clear the selection", command: "selection.clear", disabled: !s.selection },
        { label: "Fold or unfold the group", command: "group.fold", disabled: s.selection?.type !== "group" && s.selection?.type !== "node" },
        { label: "Rename the group", command: "group.rename", disabled: s.selection?.type !== "group" },
        { label: "Delete the selection", command: "selection.delete", disabled: !s.selection, danger: true },
      ],
    },
    {
      menu: "view",
      section: "Graph",
      order: 10,
      entries: () => [
        ...view.views().map((mode) => ({ label: VIEW_LABELS[mode], checked: view.mode() === mode, disabled: view.views().length < 2, action: () => view.setMode(mode) })),
        { label: "Next view", command: "view.diagram", disabled: view.views().length < 2 },
        { label: "3D", command: "view.3d", checked: s.dimension === "3d", disabled: view.mode() !== "nodal" },
        { label: "Fit the view", command: "view.fit" },
      ],
    },
    {
      menu: "view",
      section: "Show",
      order: 20,
      entries: () => [
        { label: "Labels", checked: s.labels, action: () => view.setLabels(!s.labels) },
        { label: "Groups", checked: s.groups, action: () => view.setGroups(!s.groups) },
        { label: "Bundle links between groups", checked: s.bundles, disabled: !s.groups, action: () => view.setBundles(!s.bundles) },
      ],
    },
  ];
}

/** The toolbar's link tool. */
export function viewToolbar(view: View): ToolbarButton[] {
  return [{ id: "tool-link", section: "create", order: 30, icon: "link", label: "Link", title: "Link tool: drag from a node to another", command: "tool.link", pressed: () => view.state.linkTool }];
}

/** The nodes a selection holds (one, several, or none). */
export function selectedNodeIds(selection: Selection): string[] {
  if (selection?.type === "node") return [selection.id];
  if (selection?.type === "nodes") return selection.ids;
  return [];
}

/**
 * The view's shortcut commands: the selection, the link tool, fitting the view, 2D/3D, the next
 * view (key D), the search.
 * Deleting the selection is an edit, so it goes through `actions`; `showWindow` shows the
 * Filters window, which holds the search field.
 */
export function viewCommands(view: View, actions: Actions, graph: GraphMirror, showWindow: (id: string) => void = () => {}): CommandSet {
  /** The group the selection points at: the selected group, or the selected node's group. */
  const selectedGroup = (): Group | null => {
    const s = view.state.selection;
    const g = graph.state.graph;
    const id = s?.type === "group" ? s.id : s?.type === "node" ? g.nodes.find((n) => n.id === s.id)?.group : null;
    return id ? (g.groups.find((x) => x.id === id) ?? null) : null;
  };
  return {
    "group.fold": () => {
      const group = selectedGroup();
      if (!group) return void toast("Select a group (or one of its nodes) to fold it.");
      void actions.updateGroup(group.id, { collapsed: !group.collapsed }).then((ok) => ok && view.selectGroup(group.id));
    },
    "group.rename": () => {
      const group = selectedGroup();
      if (group) view.renameGroup(group.id);
    },
    "selection.delete": () => {
      const selection = view.state.selection;
      if (selection?.type === "link") void actions.removeLink(selection.link).then((ok) => ok && view.selectLink(null));
      if (selection?.type === "group") void actions.removeGroup(selection.id).then((ok) => ok && view.selectGroup(null));
      // On the board, items go like any drawing's (Ctrl+Z brings them back).
      const ids = selection?.type === "node" ? [selection.id] : selection?.type === "nodes" ? selection.ids : [];
      if (view.mode() === "board" && ids.length) void actions.removeNodes(ids).then((ok) => ok && view.selectNode(null));
    },
    "selection.clear": () => {
      if (view.state.linkTool) view.setLinkTool(false);
      else view.selectNode(null);
    },
    "tool.link": () => view.setLinkTool(!view.state.linkTool),
    "view.fit": () => view.requestFit(),
    "view.3d": () => view.setDimension(view.state.dimension === "3d" ? "2d" : "3d"),
    "view.diagram": () => {
      // Key D goes round the views offered here.
      const offered = view.views();
      const at = offered.indexOf(view.mode() ?? offered[0]!);
      if (offered.length > 1) view.setMode(offered[(at + 1) % offered.length]!);
    },
    // The search field is in the Filters window: shown first (it may be closed or a hidden tab).
    "search.focus": () => {
      showWindow("filters");
      queueMicrotask(() => document.getElementById("search")?.focus());
    },
  };
}
